import type { DriveStep } from 'driver.js'
import type { TutorialFeatureFlags } from './tutorialFlags'
import { getTutorialTotalSteps } from './tutorialFlags'

const PREPARE_ORDER_COPY_ZEBRA
  = '<h3>Prepare Order</h3><p>When an order moves to \'On the way,\' start preparing it for the customer\'s arrival. The Scan button shows how many crates have been scanned. The 🥶 emoji is a reminder for frozen crates, while 🆔 indicates restricted items requiring a customer ID check. To view order details and crate information, simply click on the card.</p>'

const PREPARE_ORDER_COPY_DEFAULT
  = '<h3>Prepare Order</h3><p>When an order moves to "On the way," start preparing it for the customer\'s arrival. The 🥶 emoji is a reminder for frozen crates, while 🆔 indicates restricted items requiring a customer ID check. To view order details and crate information, simply click on the card.</p>'

const PARTNER_COPY
  = '<h3>Partner Delivery</h3><p>Grouped Partner orders have a blue ring for easy identification. Click the order ticket to see crates to consolidate, or tap \'Scan\' to scan them. When you finish scanning one order, the next opens straight away. If a driver is collecting multiple orders, request they be separated.</p>'

const ACKNOWLEDGE_COPY
  = '<h3>Acknowledging Orders</h3><p>When an order moves to \'Arrived,\' it appears with a green border. Tap \'We\'re on it\' to acknowledge it, which will notify the customer that their order is being prepared, keeping them informed.</p>'

const DELIVER_COPY
  = '<h3>Deliver and Complete</h3><p>When you transition an order to \'Delivering,\' it appears at the top, with the most urgent order first. Upon reaching the customers, tap \'Complete\' to update the status and finalise the tracked time.</p>'

const ORDER_DETAILS_COPY
  = '<h3>Order Details</h3><p>To view order details, simply click on any card and it will bring you to this screen. Here you can view crate information, van/sequence numbers, manually update the order status and view information.</p>'

function stepTitle(n: number, total: number) {
  return `Step ${n} of ${total}`
}

/**
 * Resolve the first matching onboarding target. Driver.js falls back to a 0×0 dummy
 * (no visible spotlight) when `document.querySelector` misses — so prefer an explicit
 * multi-candidate resolver over a single brittle selector.
 */
export function resolveOnboardingElement(
  ...selectors: string[]
): Element | undefined {
  for (const selector of selectors) {
    const el = document.querySelector(selector)
    if (el) {
      return el
    }
  }
  return undefined
}

function introStep(): DriveStep {
  return {
    element: '[data-fake="true"]',
    popover: {
      title: 'How to guide',
      description: '<h3>Team member dashboard</h3><p>This onboarding tutorial will guide you through the Team Member Dashboard. You\'ll learn how to successfully prepare, acknowledge and deliver an order.</p>',
      side: 'bottom',
      align: 'center',
    },
  }
}

/**
 * Dashboard steps shown before the Order Details / scan modal handoff.
 * Zebra: intro + Steps 1–5. Classic: intro + Steps 1–7 (includes acknowledge + deliver).
 */
export function getDashboardTutorialSteps(
  flags: TutorialFeatureFlags,
  isMobile: boolean,
): DriveStep[] {
  const total = getTutorialTotalSteps(flags)
  const zebra = flags.zebraBarcodeEnabled

  const steps: DriveStep[] = [
    introStep(),
    {
      element: '[data-onboarding="header-actions"]',
      popover: {
        title: stepTitle(1, total),
        description: '<h3>Main menu, Sound, Search</h3><p>The main menu gives you access to Active Orders, Overdue Orders, and Order History. The speaker icon mutes/unmutes notification chimes. Use the search button to find orders.</p>',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-onboarding="rapid-orders-widget"]',
      popover: {
        title: stepTitle(2, total),
        description: '<h3>Rapid orders</h3><p>Click the box to view active orders where picking has not yet been completed.</p>',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-onboarding="wait-times-widget"]',
      popover: {
        title: stepTitle(3, total),
        description: '<h3>Wait Times</h3><p>Average wait time for all customer orders are displayed here. Our target wait time is 5-minutes.</p>',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      // Prefer Scan CTA; fall back to the card so Driver.js still spotlights something.
      element: zebra
        ? () => resolveOnboardingElement(
            '[data-onboarding="section-on-the-way"] [data-onboarding="order-card-scan-button"]',
            '[data-onboarding="section-on-the-way"] [data-onboarding="order-card"]',
            '[data-onboarding="order-card-scan-button"]',
            '[data-onboarding="order-card"]',
          )
        : () => resolveOnboardingElement(
            '[data-onboarding="section-on-the-way"] [data-onboarding="order-card"]',
            '[data-onboarding="order-card"]',
          ),
      popover: {
        title: stepTitle(4, total),
        description: zebra ? PREPARE_ORDER_COPY_ZEBRA : PREPARE_ORDER_COPY_DEFAULT,
        // Mobile: keep the tip below the Scan CTA so it doesn't cover the highlight.
        side: isMobile ? 'bottom' : 'left',
        align: 'start',
      },
    },
    {
      // Partner step: spotlight the whole group card (not only the Scan CTA).
      element: () => resolveOnboardingElement(
        '[data-onboarding="section-on-the-way"] [data-onboarding="group-order-card"]',
        '[data-onboarding="group-order-card"]',
      ),
      popover: {
        title: stepTitle(5, total),
        description: PARTNER_COPY,
        // Mobile: keep the tip below the Partner card so it doesn't cover the highlight.
        side: isMobile ? 'bottom' : 'left',
        align: 'start',
      },
    },
  ]

  if (!zebra) {
    steps.push(
      {
        element: '[data-onboarding="section-arrived"] [data-onboarding="order-card"]',
        popover: {
          title: stepTitle(6, total),
          description: ACKNOWLEDGE_COPY,
          side: isMobile ? 'top' : 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-onboarding="section-delivering"] [data-onboarding="order-card"]',
        popover: {
          title: stepTitle(7, total),
          description: DELIVER_COPY,
          side: 'bottom',
          align: 'start',
        },
      },
    )
  }

  return steps
}

/**
 * Zebra modal steps 6–11 (single Order Details session, progressive phases).
 */
export function getZebraModalTutorialSteps(
  flags: TutorialFeatureFlags,
  isMobile: boolean,
): DriveStep[] {
  const total = getTutorialTotalSteps(flags)

  // Prefer beside the highlight so the popover does not cover crate rows / CTAs.
  const beside = isMobile ? 'top' as const : 'left' as const

  return [
    {
      // Scan crates CTA on both breakpoints (mobile tip parks just above the button).
      element: () => resolveOnboardingElement(
        '[data-onboarding="update-status-button"]',
        '[data-onboarding="tutorial-crate-progress"]',
        '[data-onboarding="order-details-modal-content"]',
      ),
      popover: {
        title: stepTitle(6, total),
        description: '<h3>Order Crates</h3><p>This screen lists every crate in the order by type. Tap \'Scan crates\' to start. The total at the top shows how many crates you\'ve scanned so far.</p>',
        // Mobile: tip just above the Scan crates highlight.
        side: isMobile ? 'top' : beside,
        align: 'start',
      },
    },
    {
      element: '[data-onboarding="tutorial-bulk-ambient-row"]',
      popover: {
        title: stepTitle(7, total),
        description: '<h3>Scanned Crates</h3><p>Each scanned crate is ticked in green. Can\'t find a crate? Tap any underlined row, like Bulk Ambient, to mark it as missing.</p>',
        side: beside,
        align: 'start',
      },
    },
    {
      element: () => resolveOnboardingElement(
        '[data-onboarding="tutorial-missing-crates-modal"]',
        '[data-onboarding="tutorial-missing-crates-sheet"]',
      ),
      popover: {
        title: stepTitle(8, total),
        description: '<h3>Missing Crates</h3><p>Choose why the crate is missing and tap \'Submit\'. In this example, the crate was merged with another crate.</p>',
        side: isMobile ? 'top' : 'right',
        align: 'start',
      },
    },
    {
      element: () => resolveOnboardingElement(
        '[data-onboarding="tutorial-missing-crates-modal"]',
        '[data-onboarding="tutorial-missing-crates-success"]',
      ),
      popover: {
        title: stepTitle(9, total),
        description: '<h3>Missing Crate Recorded</h3><p>A message confirms the missing crate has been recorded. It closes on its own after a few seconds.</p>',
        side: isMobile ? 'top' : 'right',
        align: 'start',
      },
    },
    {
      element: () => resolveOnboardingElement(
        '[data-onboarding="update-status-button"]',
        '[data-onboarding="order-details-modal-content"]',
      ),
      popover: {
        title: stepTitle(10, total),
        description: '<h3>All Crates Accounted For</h3><p>When every crate is scanned or marked as missing, you\'re ready to hand over. Found a missing crate? Just scan it. Tap \'Delivering to customer\' to continue.</p>',
        // Desktop: tip beside the CTA (right preferred).
        side: isMobile ? 'top' : 'right',
        align: 'start',
      },
    },
    {
      element: '[data-onboarding="order-status-trigger"]',
      popover: {
        title: stepTitle(11, total),
        description: ORDER_DETAILS_COPY,
        side: isMobile ? 'bottom' : 'left',
        align: 'start',
      },
    },
  ]
}

/**
 * After the Zebra modal: Acknowledge (12) + Deliver (13).
 */
export function getZebraPostModalDashboardSteps(
  flags: TutorialFeatureFlags,
  isMobile: boolean,
): DriveStep[] {
  const total = getTutorialTotalSteps(flags)

  return [
    {
      element: '[data-onboarding="section-arrived"] [data-onboarding="order-card-action-button"]',
      popover: {
        title: stepTitle(12, total),
        description: ACKNOWLEDGE_COPY,
        // Mobile: tip below the We're on it highlight.
        side: isMobile ? 'bottom' : 'left',
        align: 'start',
      },
    },
    {
      // Spotlight the whole Delivering card (not only the Complete CTA).
      element: () => resolveOnboardingElement(
        '[data-onboarding="section-delivering"] [data-onboarding="order-card"]',
        '[data-onboarding="section-delivering"] [data-onboarding="order-card-complete-button"]',
        '[data-onboarding="section-delivering"] [data-onboarding="order-card-action-button"]',
        '[data-onboarding="order-card"]',
      ),
      popover: {
        title: stepTitle(13, total),
        description: DELIVER_COPY,
        // Desktop: keep natural size to the left of the card (below shrinks/clips the copy).
        side: isMobile ? 'bottom' : 'left',
        align: isMobile ? 'center' : 'start',
      },
    },
  ]
}

/**
 * Classic (non-Zebra) modal steps: optional collection code + Order Details.
 */
export function getClassicModalTutorialSteps(
  flags: TutorialFeatureFlags,
  isMobile: boolean,
): DriveStep[] {
  const total = getTutorialTotalSteps(flags)
  const steps: DriveStep[] = []

  let stepNum = 8
  if (flags.collectCodeEnabled) {
    steps.push({
      element: '[data-onboarding="tutorial-collection-code-card"]',
      popover: {
        title: stepTitle(stepNum, total),
        description: '<h3>Collection code</h3><p>Before completing a Click & Collect order, ask the customer for their 4-digit collection code and enter it here. If they can\'t provide the code, tap "Can\'t provide code?" to log a reason and continue.</p>',
        side: 'bottom',
        align: 'center',
      },
    })
    stepNum += 1
  }

  steps.push({
    // Desktop + mobile: spotlight Order Details content (mobile Done CTA alone often has no cutout).
    element: () => resolveOnboardingElement(
      '[data-onboarding="order-details-modal-content"]',
      '[data-onboarding="order-details-modal"]',
      '[data-onboarding="update-status-button"]',
    ),
    popover: {
      title: stepTitle(stepNum, total),
      description: ORDER_DETAILS_COPY,
      // Desktop: tip to the left (avoids covering Status/Type). Mobile: above the panel.
      side: isMobile ? 'top' : 'left',
      align: isMobile ? 'center' : 'start',
    },
  })

  return steps
}

export function getInitialsTutorialStep(
  flags: TutorialFeatureFlags,
  isMobile = false,
): DriveStep {
  const total = getTutorialTotalSteps(flags)
  const stepNum = flags.zebraBarcodeEnabled
    ? 14
    : (flags.collectCodeEnabled ? 10 : 9)

  return {
    // Mobile: spotlight the whole TMI bottom sheet; desktop keeps the form panel.
    element: () => resolveOnboardingElement(
      ...(isMobile
        ? [
            '[data-onboarding="tutorial-initials-modal"]',
            '[data-onboarding="tutorial-initials-form"]',
          ]
        : [
            '[data-onboarding="tutorial-initials-form"]',
          ]),
    ),
    popover: {
      title: stepTitle(stepNum, total),
      description: '<h3>Enter Your Initials</h3><p>Enter your initials and tap \'Submit\' to sign off the order.</p>',
      // Desktop: tip beside the initials panel (right preferred); mobile above the sheet.
      side: isMobile ? 'top' : 'right',
      align: isMobile ? 'center' : 'start',
    },
  }
}

/** Index in the first dashboard driver at which we hand off to the modal. */
export function getModalHandoffActiveIndex(flags: TutorialFeatureFlags): number {
  // Driver activeIndex is 0-based including intro.
  // Zebra: intro(0) + steps1–5 → handoff after step 5 → activeIndex 5 when Next is clicked means currentStep = 6...
  // onNextClick uses (activeIndex || 0) + 1 as currentStep, and hands off when currentStep === lastDashboardStep+1
  // Last numbered dashboard step before modal: zebra=5, classic=7
  // When on that step, activeIndex = that number (intro is index 0, step1 is index 1, ... step5 is index 5)
  // onNextClick: currentStep = activeIndex + 1. When viewing step 5, activeIndex=5, currentStep=6 → wait that's wrong.

  // Looking at existing code: if (currentStep === 8) when activeIndex is 7
  // Steps array: [intro, s1, s2, s3, s4, s5, s6, s7] indices 0-7
  // When highlighting step 7 (Deliver), activeIndex=7, currentStep=8 → open modal. Correct.

  // Zebra steps: [intro, s1, s2, s3, s4, s5] indices 0-5
  // When highlighting step 5, activeIndex=5, currentStep=6 → open modal.
  return flags.zebraBarcodeEnabled ? 5 : 7
}
